/* Original procedural relief. Scroll requests one frame; nothing runs at rest. */
(() => {
 'use strict';
 const controllers=new Map(), reduced=matchMedia('(prefers-reduced-motion: reduce)');
 const clamp=(v,a=0,b=1)=>Math.max(a,Math.min(b,v));
 const height=(x,z)=>{
  const peak=(cx,cz,sx,sz,a)=>a*Math.exp(-((x-cx)**2/sx**2+(z-cz)**2/sz**2));
  const mass=peak(.06,-.05,.47,.62,1.02)+peak(.52,-.4,.29,.36,.66)+peak(-.5,.27,.34,.31,.55)+peak(-.47,-.45,.32,.35,.31);
  return mass*(.86+.09*Math.sin(10*x+6*z)+.047*Math.sin(21*x-14*z)+.022*Math.cos(37*x+23*z));
 };
 function relief(canvas){
  const gl=canvas.getContext('webgl',{alpha:true,antialias:true,premultipliedAlpha:false,powerPreference:'low-power'});
  if(!gl)return null;
  const derivatives=gl.getExtension('OES_standard_derivatives');
  const vertex=`attribute vec3 aPosition;attribute vec3 aNormal;
   uniform float uYaw,uTilt,uAspect,uZoom;
   varying vec3 vNormal;varying float vHeight;varying vec2 vGround;
   void main(){
    float c=cos(uYaw),s=sin(uYaw);float x=aPosition.x*c-aPosition.z*s,z=aPosition.x*s+aPosition.z*c;
    float y=z*sin(uTilt)-aPosition.y*.82*cos(uTilt);
    float depth=z*cos(uTilt)+aPosition.y*.82*sin(uTilt);
    gl_Position=vec4(x*uZoom/uAspect,-y*uZoom-.06,-depth*.3,1.);
    vHeight=aPosition.y;vNormal=aNormal;vGround=aPosition.xz;
   }`;
  const fragment=(derivatives?'#extension GL_OES_standard_derivatives : enable\n':'')+`
   precision mediump float;varying vec3 vNormal;varying float vHeight;varying vec2 vGround;
   void main(){
    vec3 n=normalize(vNormal);float sun=max(dot(n,normalize(vec3(-.6,1.,-.65))),0.);
    float elevation=clamp(vHeight/1.05,0.,1.);
    vec3 base=mix(vec3(.078,.145,.151),vec3(.28,.35,.30),smoothstep(0.,.6,elevation));
    base=mix(base,vec3(.80,.47,.25),smoothstep(.2,.9,elevation)*sun*.85);
    base*=.48+.75*sun;
    float h=vHeight*29.;float band=abs(fract(h)-.5);
    float width=${derivatives?'max(fwidth(h)*.72,.022)':'.045'};
    float line=1.-smoothstep(width,width*2.2,band);
    float major=1.-step(.55,mod(floor(h),5.));
    base=mix(base,vec3(.85,.85,.70),line*(.23+major*.27));
    float fade=1.-smoothstep(.86,1.24,max(abs(vGround.x),abs(vGround.y)));
    gl_FragColor=vec4(base,fade);
   }`;
  const shaders=[];
  const compile=(type,source)=>{const s=gl.createShader(type);gl.shaderSource(s,source);gl.compileShader(s);shaders.push(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw new Error('Terrain shader unavailable');return s;};
  const program=gl.createProgram();
  try{gl.attachShader(program,compile(gl.VERTEX_SHADER,vertex));gl.attachShader(program,compile(gl.FRAGMENT_SHADER,fragment));gl.linkProgram(program);if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw new Error('Terrain unavailable');}catch{shaders.forEach(s=>gl.deleteShader(s));gl.deleteProgram(program);return null;}
  const positions=[],normals=[],indices=[],size=116,delta=.003;
  for(let j=0;j<=size;j++)for(let i=0;i<=size;i++){
   const x=i/size*2.5-1.25,z=j/size*2.5-1.25;
   positions.push(x,height(x,z),z);
   normals.push(-(height(x+delta,z)-height(x-delta,z))/(2*delta)*.82,1,-(height(x,z+delta)-height(x,z-delta))/(2*delta)*.82);
  }
  for(let j=0;j<size;j++)for(let i=0;i<size;i++){const a=j*(size+1)+i,b=a+size+1;indices.push(a,b,a+1,a+1,b,b+1);}
  gl.useProgram(program);
  const buffers=[];
  const attribute=(name,values)=>{const b=gl.createBuffer();buffers.push(b);gl.bindBuffer(gl.ARRAY_BUFFER,b);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array(values),gl.STATIC_DRAW);const loc=gl.getAttribLocation(program,name);gl.enableVertexAttribArray(loc);gl.vertexAttribPointer(loc,3,gl.FLOAT,false,0,0);};
  attribute('aPosition',positions);attribute('aNormal',normals);
  const index=gl.createBuffer();buffers.push(index);gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,index);gl.bufferData(gl.ELEMENT_ARRAY_BUFFER,new Uint16Array(indices),gl.STATIC_DRAW);
  const uniforms=Object.fromEntries(['uYaw','uTilt','uAspect','uZoom'].map(k=>[k,gl.getUniformLocation(program,k)]));
  gl.enable(gl.DEPTH_TEST);gl.enable(gl.BLEND);gl.blendFunc(gl.SRC_ALPHA,gl.ONE_MINUS_SRC_ALPHA);gl.clearColor(0,0,0,0);
  return {
   draw(p,w,h){
    const ratio=Math.min(devicePixelRatio||1,1.5),cw=Math.round(w*ratio),ch=Math.round(h*ratio);
    if(canvas.width!==cw||canvas.height!==ch){canvas.width=cw;canvas.height=ch;}
    const camera={yaw:-.32+p*.42,tilt:1.08-p*.48,zoom:.90+p*.09,aspect:w/h};
    gl.viewport(0,0,cw,ch);gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);
    gl.uniform1f(uniforms.uYaw,camera.yaw);gl.uniform1f(uniforms.uTilt,camera.tilt);gl.uniform1f(uniforms.uAspect,camera.aspect);gl.uniform1f(uniforms.uZoom,camera.zoom);
    gl.drawElements(gl.TRIANGLES,indices.length,gl.UNSIGNED_SHORT,0);return camera;
   },
   destroy(){buffers.forEach(b=>gl.deleteBuffer(b));shaders.forEach(s=>gl.deleteShader(s));gl.deleteProgram(program);}
  };
 }
 const waypoints=[[-.73,.65],[-.48,.56],[-.34,.31],[-.11,.37],[.04,.23],[-.13,.12],[.03,.0],[.11,-.1]],route=[];
 for(let i=0;i<waypoints.length-1;i++)for(let j=0;j<22;j++){
  const t=j/22,a=waypoints[Math.max(0,i-1)],b=waypoints[i],c=waypoints[i+1],d=waypoints[Math.min(waypoints.length-1,i+2)];
  const xy=[0,1].map(k=>.5*((2*b[k])+(-a[k]+c[k])*t+(2*a[k]-5*b[k]+4*c[k]-d[k])*t*t+(-a[k]+3*b[k]-3*c[k]+d[k])*t*t*t));
  route.push([xy[0],height(...xy)+.014,xy[1]]);
 }
 function init(root=document){
  root.querySelectorAll('[data-terrain-story]').forEach(section=>{
   if(controllers.has(section))return;
   const view=section.querySelector('.terrain-view'),canvas=view.querySelector('canvas'),svg=view.querySelector('svg'),line=svg.querySelector('.terrain-route__line'),glow=svg.querySelector('.terrain-route__shadow'),dot=svg.querySelector('circle');
   const events=new AbortController(),signal=events.signal;
   let renderer=null,attempted=false,visible=false,frame=0;
   const motion=()=>!reduced.matches&&document.body.dataset.topographyMotion!=='false';
   function draw(){
    frame=0;if(!visible||document.hidden)return;
    const rect=section.getBoundingClientRect(),vh=innerHeight,pinned=motion()&&!section.classList.contains('is-static')&&innerWidth>900;
    const progress=motion()?clamp(pinned?-rect.top/Math.max(rect.height-vh,1):(vh-rect.top)/(vh+rect.height)):.7;
    section.style.setProperty('--terrain-progress',progress.toFixed(4));
    if(!attempted){attempted=true;renderer=relief(canvas);}
    if(!renderer){section.classList.add('is-static');return;}
    const {width:w,height:h}=view.getBoundingClientRect();if(!w||!h)return;
    const camera=renderer.draw(progress,w,h),c=Math.cos(camera.yaw),s=Math.sin(camera.yaw);
    const projected=route.map(([x,y,z])=>{
     const rx=x*c-z*s,rz=x*s+z*c;
     return [(rx*camera.zoom/camera.aspect+1)*w/2,((rz*Math.sin(camera.tilt)-y*.82*Math.cos(camera.tilt))*camera.zoom+.06+1)*h/2];
    });
    const count=Math.max(2,Math.round((.16+progress*.84)*projected.length));
    const d=projected.slice(0,count).map(([x,y],i)=>(i?'L':'M')+x.toFixed(2)+','+y.toFixed(2)).join(' ');
    svg.setAttribute('viewBox',`0 0 ${w} ${h}`);line.setAttribute('d',d);glow.setAttribute('d',d);
    const tip=projected[count-1];dot.setAttribute('cx',tip[0]);dot.setAttribute('cy',tip[1]);view.classList.add('is-rendered');
   }
   const request=()=>{if(!frame&&visible)frame=requestAnimationFrame(draw);};
   const sync=()=>{document.documentElement.classList.toggle('topography-motion',motion());request();};
   const observer=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;request();},{rootMargin:'100px'});observer.observe(section);
   const resize=new ResizeObserver(request);resize.observe(view);
   window.addEventListener('scroll',request,{passive:true,signal});window.addEventListener('resize',request,{signal});document.addEventListener('visibilitychange',request,{signal});reduced.addEventListener('change',sync,{signal});
   canvas.addEventListener('webglcontextlost',event=>{event.preventDefault();view.classList.remove('is-rendered');section.classList.add('is-static');renderer=null;},{signal});
   canvas.addEventListener('webglcontextrestored',()=>{attempted=false;section.classList.remove('is-static');request();},{signal});
   sync();controllers.set(section,()=>{events.abort();observer.disconnect();resize.disconnect();cancelAnimationFrame(frame);renderer?.destroy();});
  });
 }
 init();
 document.addEventListener('shopify:section:load',event=>init(event.target));
 document.addEventListener('shopify:section:unload',event=>controllers.forEach((dispose,section)=>{if(event.target.contains(section)){dispose();controllers.delete(section);}}));
})();
